import { useState, FormEvent } from 'react';

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    jobAddress: '',
    projectType: '',
    projectDescription: '',
    timeline: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('FORM_SUBMIT_URL_PLACEHOLDER', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          jobAddress: '',
          projectType: '',
          projectDescription: '',
          timeline: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="quote-form" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-black mb-4">
            Request a Custom Quote
          </h2>
          <p className="text-xl text-gray-700">
            Tell us about your project and we'll provide a detailed estimate tailored to your needs.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border-2 border-yellow-500"
          data-readdy-form
          id="custom-masonry-quote-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-bold text-black mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-bold text-black mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
                placeholder="(828) 555-0123"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-bold text-black mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="john@example.com"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="jobAddress" className="block text-sm font-bold text-black mb-2">
              Job Address *
            </label>
            <input
              type="text"
              id="jobAddress"
              name="jobAddress"
              value={formData.jobAddress}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="123 Main Street, Otto, NC 28763"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="projectType" className="block text-sm font-bold text-black mb-2">
              Type of Custom Project *
            </label>
            <select
              id="projectType"
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
            >
              <option value="">Select project type</option>
              <option value="Specialty Concrete Finish">Specialty Concrete Finish</option>
              <option value="Decorative Masonry">Decorative Masonry</option>
              <option value="Repair & Restoration">Repair & Restoration</option>
              <option value="Custom Design Project">Custom Design Project</option>
              <option value="Grading & Site Preparation">Grading & Site Preparation</option>
              <option value="Mixed Services">Mixed Services</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="mb-6">
            <label htmlFor="timeline" className="block text-sm font-bold text-black mb-2">
              Desired Timeline
            </label>
            <input
              type="text"
              id="timeline"
              name="timeline"
              value={formData.timeline}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm"
              placeholder="e.g., Within 2 weeks, Next month, Flexible"
            />
          </div>

          <div className="mb-8">
            <label htmlFor="projectDescription" className="block text-sm font-bold text-black mb-2">
              Project Description *
            </label>
            <textarea
              id="projectDescription"
              name="projectDescription"
              value={formData.projectDescription}
              onChange={handleChange}
              required
              maxLength={500}
              rows={5}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-yellow-500 focus:outline-none text-sm resize-none"
              placeholder="Please describe your custom masonry project in detail..."
            ></textarea>
            <p className="text-xs text-gray-500 mt-2">
              {formData.projectDescription.length}/500 characters
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
              <p className="text-green-700 font-bold flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-xl"></i>
                Quote request submitted successfully! We'll contact you soon.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
              <p className="text-red-700 font-bold flex items-center gap-2">
                <i className="ri-error-warning-fill text-xl"></i>
                Something went wrong. Please try again or call us directly.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-yellow-500 text-black px-8 py-4 rounded-lg font-bold hover:bg-yellow-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT QUOTE REQUEST'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default QuoteForm;